<script setup>
import { computed, watch } from "vue"
import { useStore } from "vuex"
import { useRoute } from "vue-router"
import { getUserDisplayName } from "@common/helpers"
import { Snackbar } from "@projetsolaire/ui"
import CookieConsentDialog from "@/components/CookieConsentDialog.vue"

const store = useStore()
const route = useRoute()

// --- Environment config ---
const intercomAppId = import.meta.env.VITE_INTERCOM_APP_ID
const intercomEnabled = import.meta.env.VITE_INTERCOM_ENABLED === "true"

// --- State ---
const user = computed(() => store.state.auth.user)

// --- Iframe detection ---
const isIframe = computed(() => {
  return route.query.iframe === "true" || window.self !== window.top
})

// --- Route-based exclusion ---
const shouldBootIntercom = computed(() => {
  if (!intercomEnabled) return false
  if (isIframe.value) return false

  const excludedRouteNames = ["InstallerHostedPageParent"]
  const hasExcludedMatch = route.matched?.some((r) =>
    excludedRouteNames.includes(r.name),
  )

  return !hasExcludedMatch
})

// --- Helpers ---
const getIntercomName = (user) => {
  if (user.valueType === "installer") return user.companyName
  return getUserDisplayName(user)
}

// --- Boot state guard ---
let hasBootedIntercom = false

const bootIntercom = () => {
  if (hasBootedIntercom) return
  hasBootedIntercom = true

  window.Intercom("boot", {
    app_id: intercomAppId,
    user_id: user.value ? user.value.id : null,
    name: user.value ? getIntercomName(user.value) : null,
    email: user.value ? user.value.email : null,
  })
}

// --- Boot Intercom when allowed ---
watch(
  () => shouldBootIntercom.value,
  (canBoot) => {
    if (canBoot) bootIntercom()
  },
  { immediate: true },
)

// --- Update user info after boot ---
watch(
  () => user.value,
  (val) => {
    if (!shouldBootIntercom.value) return
    if (!hasBootedIntercom) return
    if (!window.Intercom) return

    window.Intercom("update", {
      email: val ? val.email : null,
      user_id: val ? val.id : null,
      name: val ? getIntercomName(val) : null,
    })
  },
  { immediate: true },
)
</script>

<template>
  <div>
    <Snackbar />
    <router-view />
    <CookieConsentDialog v-if="!isIframe" />
  </div>
</template>
